/**
 * An action screen that is used by join-widget.jsx to direct users who have
 * arrived via /join/XXXXX (with the class code in the URL)
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import {PermissionsInfo} from "@khan/shared-core";

import ProgressShellActionScreen from "#shared/auth/progress-shell/progress-shell-action-screen.tsx";

import LoggedInJoinPrompt from "./logged-in-join-prompt.tsx";
import LoginAndSignupButtons from "./login-and-signup-buttons.tsx";

type Props = {
    // The class code of the class we're about to join
    classCode: string;
    // The name of the class we're about to join
    className: string;
    // Called when the user wants to join a different class
    onJoinDifferentClass: () => unknown;
    // Called when the user elects to log in with this class code
    onLoginWithCode: () => unknown;
    // Called when the user elects to sign up with this class code
    onSignupWithCode: () => unknown;
};

function JoinConfirmClassActionScreen(props: Props): React.ReactElement {
    const {
        classCode,
        className,
        onJoinDifferentClass,
        onLoginWithCode,
        onSignupWithCode,
    } = props;

    return (
        <ProgressShellActionScreen>
            <View style={styles.contents}>
                <HeadingSmall>{t`You're joining: ${className}`}</HeadingSmall>
                <Strut size={spacing.xLarge_32} />
                {PermissionsInfo.isLoggedIn ? (
                    <LoggedInJoinPrompt
                        classCode={classCode}
                        className={className}
                        onJoinDifferentClass={onJoinDifferentClass}
                    />
                ) : (
                    <>
                        <LoginAndSignupButtons
                            onLoginClick={onLoginWithCode}
                            onSignupClick={onSignupWithCode}
                        />
                        <Body style={styles.notYourClass}>
                            {t`Not your class?`}{" "}
                            <Button
                                kind="tertiary"
                                onClick={onJoinDifferentClass}
                            >
                                {t`Join a different class`}
                            </Button>
                        </Body>
                    </>
                )}
            </View>
        </ProgressShellActionScreen>
    );
}

const styles = StyleSheet.create({
    contents: {
        textAlign: "left",
        height: "100%",
    },
    notYourClass: {
        display: "block",
        marginTop: 16,
        textAlign: "center",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    shim8: {
        height: 8,
    },
});

export default JoinConfirmClassActionScreen;
